import { useState } from 'react';
import { useTable, type ColumnDef } from '@tanstack/react-table';
import { MdOutlineClose } from 'react-icons/md';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Select from '@/components/Select';
import Checkbox from '@/components/Checkbox';
import { Tag } from '@/components/Tag';
import { DataTable, dataTableFeatures } from '@/components/ui/data-table';
import { useDebounce } from '@/hooks/useDebounce';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import useCopyTargetCandidates from '../hooks/useCopyTargetCandidates';
import { formatLastOfferedAt } from '../utils/formatLastOfferedAt';
import type { AgentApiCandidateEndpointsCopyTargetCandidate } from '@/types/serializers';

type Props = {
  candidateProfileId: number;
  selectedTargetCandidates: AgentApiCandidateEndpointsCopyTargetCandidate[];
  setSelectedTargetCandidates: (
    candidates: AgentApiCandidateEndpointsCopyTargetCandidate[]
  ) => void;
};

export default function CandidatesOfferCopyTargetContents({
  candidateProfileId,
  selectedTargetCandidates,
  setSelectedTargetCandidates,
}: Props) {
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [assigneeId, setAssigneeId] = useState<number | undefined>(undefined);
  const keywordDebounce = useDebounce(300);

  const { data: activeUsersData } = useFetchActiveUsers();
  const { data, isLoading } = useCopyTargetCandidates({
    keyword,
    assignee_id: assigneeId,
    candidate_profile_id: candidateProfileId,
  });

  const candidates = data?.records ?? [];

  const assigneeItems = [
    { value: 'all', label: '全て表示' },
    ...(activeUsersData?.records.map((user) => ({ value: String(user.id), label: user.name })) ??
      []),
  ];

  const hasSearchConditions = keywordInput !== '' || assigneeId != null;

  const handleClearSearch = () => {
    setKeywordInput('');
    setKeyword('');
    setAssigneeId(undefined);
    keywordDebounce(() => setKeyword(''));
  };

  const isSelected = (candidate: AgentApiCandidateEndpointsCopyTargetCandidate) =>
    selectedTargetCandidates.some((c) => c.id === candidate.id);

  const handleSelectionChange = (
    candidate: AgentApiCandidateEndpointsCopyTargetCandidate,
    selected: boolean
  ) => {
    if (selected) {
      setSelectedTargetCandidates([...selectedTargetCandidates, candidate]);
    } else {
      setSelectedTargetCandidates(selectedTargetCandidates.filter((c) => c.id !== candidate.id));
    }
  };

  const columns: ColumnDef<
    typeof dataTableFeatures,
    AgentApiCandidateEndpointsCopyTargetCandidate
  >[] = [
    {
      id: 'candidate',
      header: () => <span className="block pl-xl">求職者氏名 / 担当者</span>,
      cell: ({ row }) => (
        <div className="flex items-center gap-sm">
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected(row.original)}
              onChange={(checked) => handleSelectionChange(row.original, checked)}
            />
          </div>
          <div className="flex min-w-0 items-center gap-xs">
            <span className="truncate text-heading-xxs text-main">
              {row.original.name}
              {row.original.age != null && `(${row.original.age})`}
            </span>
            {row.original.assignees.length > 0 && (
              <Tag color="default" size="sm" className="shrink-0">
                {row.original.assignees[0].name}
                {row.original.assignees.length > 1 && (
                  <span className="ml-xxs text-heading-xxs text-primary">
                    {`+${row.original.assignees.length - 1}`}
                  </span>
                )}
              </Tag>
            )}
          </div>
        </div>
      ),
      meta: { headerClassName: 'w-[27%]', cellClassName: 'w-[27%]' },
    },
    {
      id: 'job_category',
      header: '職種',
      accessorFn: (row) => row.job_category ?? '-',
      meta: { headerClassName: 'w-[26%]', cellClassName: 'w-[26%] text-main' },
    },
    {
      id: 'offered_count',
      header: '提案求人',
      accessorFn: (row) => (row.offered_count > 0 ? `${row.offered_count}件` : '-'),
      meta: { headerClassName: 'w-[22%]', cellClassName: 'w-[22%] text-main' },
    },
    {
      id: 'last_offered_at',
      header: '最終提案日時',
      accessorFn: (row) => (row.last_offered_at ? formatLastOfferedAt(row.last_offered_at) : '-'),
      meta: {
        headerClassName: 'w-[25%]',
        cellClassName: 'w-[25%] whitespace-nowrap text-main',
      },
    },
  ];

  const table = useTable({
    features: dataTableFeatures,
    data: candidates,
    columns,
  });

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end gap-lg">
        <div className="flex flex-1 flex-col gap-xs">
          <label className="text-heading-xxs text-main">フリーワード</label>
          <Input
            search
            className="w-full"
            placeholder="フリーワードで絞り込む"
            value={keywordInput}
            onChange={(e) => {
              const value = e.target.value;
              setKeywordInput(value);
              keywordDebounce(() => setKeyword(value));
            }}
          />
        </div>
        <div className="flex w-[223px] flex-col gap-xs">
          <label className="text-heading-xxs text-main">担当者</label>
          <Select
            className="w-full"
            items={assigneeItems}
            value={assigneeId ? String(assigneeId) : 'all'}
            onValueChange={(value) => setAssigneeId(value === 'all' ? undefined : Number(value))}
          />
        </div>
        <Button
          color="tertiary"
          size="md"
          type="button"
          disabled={!hasSearchConditions}
          onClick={handleClearSearch}
        >
          <MdOutlineClose className="size-icon-xs text-primary" />
          <p className="text-heading-xxs text-primary">検索条件をクリア</p>
        </Button>
      </div>
      {!isLoading && candidates.length === 0 ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-lg">
          <p className="text-heading-xs text-main">求職者データがありません</p>
          <img src="/agent/candidates/candidate-no-data.svg" width={148} height={144} alt="" />
        </div>
      ) : (
        <DataTable
          table={table}
          isLoading={isLoading}
          skeletonRowCount={12}
          tableClassName="table-fixed w-full"
          containerClassName="rounded-none"
          onRowClick={(candidate) => handleSelectionChange(candidate, !isSelected(candidate))}
          getRowSelected={(candidate) => isSelected(candidate)}
        />
      )}
    </div>
  );
}
